import React from 'react'
import styles from './Block.scss'
import { SelectionState, EditorState, KeyBindingUtil } from 'draft-js'
import { emptyBlock, insertBlockAfter } from 'lib/blockUtils'
import sizes from '../../../App/blockSizes.scss'
import cx from 'classnames'
import { getConsole } from 'lib/getConsole'
const console = getConsole('rr', 'Block')
const { hasCommandModifier } = KeyBindingUtil

const steamRollHandlers = [
  'onBeforeInput',
  'onBlur',
  'onCompositionEnd',
  'onCompositionStart',
  'onCopy',
  'onCut',
  // 'onClick',
  'onDragEnd',
  'onDragOver',
  'onDragStart',
  'onDrop',
  'onInput',
  // 'onFocus',
  // steamrolling onKeyDown prevents CMD+S from working properly.
  // disabling the steamrolling so far hasn't given me any trouble - draft
  // mostly uses onInput in modern browsers
  // 'onKeyDown',
  'onKeyPress',
  'onKeyUp',
  // the mouseup/mousedown steamrolling was causing problems with the sample
  // graph which listens for window.mouseup. Disabling it may cause problems
  // with draft though...
  // 'onMouseDown',
  // 'onMouseUp',
  'onPaste',
  'onSelect' // Cannot read property 'getIn' of undefined is caused by the
  // parent Draft instance getting an onSelect callback while the
  // caret is still in the child Draft instance. This occurs when
  // switching focus between the editors. Could be fixed by a patch
  // in the draft-js function 'getUpdatedSelectionState'.
].reduce((handlers, event) => {
  const handlerForEvent = (e) => {
    e.stopPropagation()
  }
  return { ...handlers, [event]: handlerForEvent }
}, {})

const filterKeyDown = (e) => {
  const S = 83
  const Z = 90
  // undo
  if (e.keyCode === Z && hasCommandModifier(e)) {
    return
  }
  // save
  if (e.keyCode === S && hasCommandModifier(e)) {

  } else {
    e.stopPropagation()
  }
}

export class Block extends React.PureComponent {
  static defaultProps = { grabFocusOnUpdate: true }

  componentDidMount () {
    this.grabFocusIfSelected()
  }

  componentDidUpdate (prevProps) {
    const { grabFocusOnUpdate } = this.props
    if (grabFocusOnUpdate) {
      this.grabFocusIfSelected(prevProps.blockProps.isSelected)
    }
  }

  setFocusRef = (focusRef) => {
    this._focusRef = focusRef
  }

  // TODO properly handle the case where multiple atomic blocks are selected as part of a range
  grabFocusIfSelected = (wasSelected = false) => {
    const { isSelected } = this.props.blockProps
    if (isSelected && !wasSelected) {
      console.log(this.props.block.getKey() + ' gained selection, nuking text selection ranges')
      window.getSelection().removeAllRanges()
    }
    // grab focus if we're selected and neither the focusRef or the caption is focused
    if (isSelected && this._focusRef && document.activeElement !== this._focusRef && !this._focusRef.contains(document.activeElement)) {
      console.log(this.props.block.getKey() + ' is selected, grabbing focus')
      this._focusRef.focus()
    }
  }

  onSelected = (e) => {
    const { onSelected } = this.props.blockProps
    onSelected()
    e.stopPropagation()
  }

  onKeyDown = (e) => {
    const BACKSPACE = 8
    const DOWN_ARROW = 40
    const UP_ARROW = 38
    const RETURN = 13

    switch (e.keyCode) {
      case DOWN_ARROW:
        this.onDownArrow()
        e.preventDefault()
        return
      case UP_ARROW:
        this.onUpArrow()
        e.preventDefault()
        return
      case BACKSPACE:
        if (this.removeSelfIfFirst()) {
          e.preventDefault()
        }
        return
      case RETURN:
        this.onReturn()
        e.stopPropagation()
        e.preventDefault()
        return
      default:
        filterKeyDown(e)
    }
  }

  // TODO: implement onDownArrow in the ReportEditor so that atomic blocks are
  // selected when hitting down arrow in a text block. This is actually kinda
  // tricky since we only want to override default behavior when the cursor is
  // on the last line of text before an atomic block, which is why I'm not doing
  // it in this PR.
  //
  // issue reference: https://github.com/ridewithgps/rwgps-ui/issues/197
  onDownArrow = () => {
    // set editorState selection to the start of the next block (images are
    // guaranteed to have a next block)
    const { getEditorState, setEditorState } = this.props.blockProps
    const editorState = getEditorState()
    const contentState = editorState.getCurrentContent()
    const nextBlock = contentState.getBlockAfter(this.props.block.getKey())
    const selectionState = SelectionState.createEmpty(nextBlock.getKey()).set('hasFocus', true)
    console.log('down arrow selected ' + nextBlock.getKey())
    setEditorState(EditorState.forceSelection(editorState, selectionState))
  }

  onUpArrow = () => {
    const { getEditorState, setEditorState } = this.props.blockProps
    const editorState = getEditorState()
    const contentState = editorState.getCurrentContent()
    const nextBlock = contentState.getBlockBefore(this.props.block.getKey())
    if (!nextBlock) { return }
    const selectionState = SelectionState
      .createEmpty(nextBlock.getKey()).set('hasFocus', true)
      .merge({ anchorOffset: nextBlock.getLength(), focusOffset: nextBlock.getLength() })
    setEditorState(EditorState.forceSelection(editorState, selectionState))
  }

  onReturn = () => {
    // insert empty text block below current block and put focus there
    const { getEditorState, setEditorState } = this.props.blockProps
    const editorState = getEditorState()
    const contentState = editorState.getCurrentContent()
    const currentBlock = this.props.block
    const empty = emptyBlock()
    const withEmpty = insertBlockAfter(contentState.blockMap, currentBlock.getKey(), empty)
    const selectionState = SelectionState.createEmpty(empty.getKey()).set('hasFocus', true)
    const newEditorState = EditorState.push(getEditorState(), contentState.merge({ blockMap: withEmpty }), 'split-block')
    const withSelection = EditorState.forceSelection(newEditorState, selectionState)
    setEditorState(withSelection)
  }

  // returns boolean indicating whether it removed the block.
  removeSelfIfFirst = () => {
    const { getEditorState, setEditorState } = this.props.blockProps
    const { block } = this.props
    const blockKey = block.getKey()
    let editorState = getEditorState()
    let contentState = editorState.getCurrentContent()
    let blockMap = contentState.getBlockMap()
    const blockBefore = contentState.getBlockBefore(blockKey)
    if (blockBefore) {
      return false
    }
    const blockAfterKey = contentState.getBlockAfter(blockKey).getKey()
    blockMap = blockMap.remove(blockKey)
    const selectionBefore = editorState.getSelection()
    const selectionAfter = selectionBefore.merge(new SelectionState({
      anchorKey: blockAfterKey,
      anchorOffset: 0,
      focusKey: blockAfterKey,
      focusOffset: 0,
      isBackward: false
    }))
    contentState = contentState.merge({
      blockMap,
      selectionBefore,
      selectionAfter
    })
    editorState = EditorState.push(editorState, contentState, 'remove-range')
    setEditorState(editorState)
    return true
  }

  render () {
    const { children, blockProps, size = 'normalBlock' } = this.props
    const { isSelected } = blockProps
    const className = cx(styles.Block, sizes[size], isSelected && styles.isSelected)
    return (
      <div
        tabIndex='0'
        ref={this.setFocusRef}
        className={className}
        contentEditable={false} spellCheck={false}
        onClick={!isSelected ? this.onSelected : undefined}
        onFocus={!isSelected ? this.onSelected : undefined}
        {...steamRollHandlers}
        onKeyDown={this.onKeyDown}>
        {children}
      </div>
    )
  }
}
